import { isPlatformServer, NgIf, NgFor } from '@angular/common';
import {
  Component,
  TemplateRef,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { PLATFORM_ID } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { RootState } from '@next/core-lib/app';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { PageToolbarComponent } from '@next/core-lib/components';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import { COUNTRIES } from '../../../../assets/data/Countries';
import Utils from '../../../shared/utils';
import { ReferenceValueSheet } from '../../models/reference-value-sheet.model';
import * as refValSheetActions from '../../store/reference-value-sheet.actions';
import {
  getReferenceValueSheets,
  getSelectedCountry,
} from '../../store/reference-value-sheet.selectors';

import { ReferenceValueSheetCountryDialogComponent } from '../reference-value-sheet-country-dialog/reference-value-sheet-country-dialog.component';

@Component({
  selector: 'app-reference-value-sheet-overview',
  templateUrl: './reference-value-sheet-overview.component.html',
  imports: [
    PageToolbarComponent,
    NgIf,
    MatFormField,
    MatSelect,
    NgFor,
    MatOption,
    TableComponent,
    MatSlideToggle,
    MatButton,
    TranslatePipe,
  ],
})
export class ReferenceValueSheetOverviewComponent {
  private readonly locale = inject<LocaleService>(LocaleService);
  private localeService = inject(LocaleService);
  private store = inject<Store<RootState>>(Store);
  private readonly activatedRoute = inject(ActivatedRoute);
  private dialog = inject(DialogService);
  private platformId = inject(PLATFORM_ID);

  /* STORE SIGNALS */
  private readonly routeData = toSignal(this.activatedRoute.data);
  readonly pageTitle = computed(() => {
    const data = this.routeData();
    return this.locale.translate(
      `${data?.['isStandard'] ? 'standard-' : ''}reference-value-sheet.overview.title`,
    );
  });
  readonly selectedCountry = toSignal(this.store.select(getSelectedCountry));
  private readonly refValSheetsSignal = toSignal(this.store.select(getReferenceValueSheets), {
    initialValue: [],
  });
  private readonly isAdvisorSignal = toSignal(
    this.store.pipe(select(AuthorizationSelectors.hasAtLeastAdvisorRole)),
    { initialValue: false },
  );
  private readonly userSignal = toSignal(this.store.select(UserSelectors.selectUser));
  private readonly hasAdministratorRoleSignal = toSignal(
    this.store.select(AuthorizationSelectors.hasAdministratorRole),
    { initialValue: false },
  );
  private readonly hasGlobalAdministratorRoleSignal = toSignal(
    this.store.select(AuthorizationSelectors.hasGlobalAdministratorRole),
    { initialValue: false },
  );
  private readonly selectedLocationSignal = toSignal(
    this.store.select(UserSelectors.selectLocation),
  );

  /* PUBLIC COMPUTED STATE */
  readonly refValSheets = computed(() => this.refValSheetsSignal());
  readonly isAdvisor = computed(() => this.isAdvisorSignal());
  isStandardRefValSheet = signal(false);

  /* TEMPLATES */
  columnNameTemplate = viewChild<TemplateRef<unknown>>('columnNameTemplate');
  columnActiveTemplate = viewChild<TemplateRef<unknown>>('columnActiveTemplate');

  /* TABLE CONFIG */
  tableConfig = signal<TableConfig>({
    dataSource: new MatTableDataSource([] as ReferenceValueSheet[]),
    selectColumn: false,
    pagination: false,
    scrolling: true,
    width: '1200px',
    wrapperWidth: '100%',
    columns: [
      {
        id: 'name',
        type: TableColumnTypes.TEMPLATE,
        label: this.translate('table-header.name'),
      },
      {
        id: 'created',
        type: TableColumnTypes.DATE,
        label: this.translate('table-header.created'),
      },
      {
        id: 'updated',
        type: TableColumnTypes.DATE,
        label: this.translate('table-header.updated'),
      },
      {
        id: 'updatedAdvisorName',
        type: TableColumnTypes.STRING,
        label: this.translate('table-header.updatedAdvisorName'),
      },
      {
        id: 'isActive',
        type: TableColumnTypes.TEMPLATE,
        label: this.translate('table-header.isActive'),
      },
    ],
    actions: [
      {
        id: 'edit',
        label: this.translate('buttons.edit'),
        callback: (refValSheet: ReferenceValueSheet) => {
          if (this.isAdvisor()) {
            this.store.dispatch(
              refValSheetActions.editRefValSheetNavigation({
                isStandard: this.isStandardRefValSheet(),
                refValSheet,
              }),
            );
          }
        },
        icon: 'edit',
        disabled: () => !this.isAdvisor(),
      },
      {
        id: 'delete',
        label: this.translate('buttons.delete'),
        callback: (refValSheet: ReferenceValueSheet) => {
          if (this.isAdvisor()) {
            this.store.dispatch(refValSheetActions.deleteRefValSheetConfirm({ refValSheet }));
          }
        },
        icon: 'delete',
        disabled: () => !this.isAdvisor(),
      },
      {
        id: 'copyAsNew',
        callback: (refValSheet: ReferenceValueSheet) => this.copyAsNew(refValSheet),
        label: this.translate('buttons.copy-as-new'),
        icon: 'file_copy',
        disabled: () => false,
        primary: false,
      },
      {
        id: 'copyToOtherCountry',
        callback: (refValSheet: ReferenceValueSheet) => this.copyToOtherCountry(refValSheet),
        label: this.translate('buttons.copy-to-other-country'),
        icon: 'public',
        disabled: () => !this.isStandardRefValSheet(),
        primary: false,
      },
    ],
  });

  countries: Array<{ id: string; value: string }> = COUNTRIES.sort((a, b) =>
    a.value < b.value ? -1 : 1,
  );

  isServer = isPlatformServer(this.platformId);

  constructor() {
    const data = this.routeData();
    this.isStandardRefValSheet.set(!!data?.['isStandard']);

    /** Applies templates to table columns when ViewChild templates become available */
    effect(() => {
      const nameTpl = this.columnNameTemplate();
      const activeTpl = this.columnActiveTemplate();
      if (!nameTpl || !activeTpl) return;
      untracked(() => {
        this.applyTemplates();
      });
    });

    /** Updates table data when refValSheets change */
    effect(() => {
      const refvalsheets = this.refValSheetsSignal();
      const config = this.tableConfig();
      if (refvalsheets?.length) {
        config.dataSource.data = refvalsheets;
      } else {
        config.dataSource.data = [];
      }
    });
  }

  private applyTemplates() {
    const newConfig = { ...this.tableConfig() };
    const columnName = newConfig.columns.find((c) => c.id === 'name');
    const columnActive = newConfig.columns.find((c) => c.id === 'isActive');

    if (columnName) {
      columnName.template = this.columnNameTemplate();
    }
    if (columnActive) {
      columnActive.template = this.columnActiveTemplate();
    }
    this.tableConfig.set(newConfig);
  }

  translate(key: string): string {
    return this.localeService.translate(`reference-value-sheet.overview.${key}`);
  }

  onButtonCreate() {
    if (this.isAdvisor()) {
      this.store.dispatch(
        refValSheetActions.createFromStandardRefValSheetNavigation({
          isStandard: this.isStandardRefValSheet(),
        }),
      );
    }
  }

  onToggleActive(refValSheet: ReferenceValueSheet) {
    if (this.isAdvisor()) {
      const lRefValSheet = { ...refValSheet };
      lRefValSheet.isActive = !refValSheet.isActive;
      this.store.dispatch(
        refValSheetActions.upsertRefValSheetRequest({
          refValSheet: lRefValSheet,
          upsertType: 'toggleActive',
        }),
      );
    }
  }

  onViewRefValSheet(refValSheet: ReferenceValueSheet) {
    this.store.dispatch(
      refValSheetActions.viewRefValSheetNavigation({
        isStandard: this.isStandardRefValSheet(),
        refValSheet: { ...refValSheet },
      }),
    );
  }

  copyAsNew(refValSheet: ReferenceValueSheet) {
    const refValSheets = this.refValSheetsSignal();
    const user = this.userSignal();

    const foundRefValSheet = refValSheets.find((rvs) => rvs.id === refValSheet.id);
    this.store.dispatch(
      refValSheetActions.upsertRefValSheetRequest({
        refValSheet: {
          ...foundRefValSheet,
          id: 0,
          advisorName: user.name,
          updatedAdvisorName: user.name,
          created: Utils.toUtcDate(new Date()).toISOString(),
          updated: Utils.toUtcDate(new Date()).toISOString(),
        },
        upsertType: 'copyAsNew',
      }),
    );
  }

  copyToOtherCountry(refValSheet: ReferenceValueSheet) {
    if (this.isStandardRefValSheet()) {
      const refValSheets = this.refValSheetsSignal();
      const user = this.userSignal();
      const hasAdministratorRole = this.hasAdministratorRoleSignal();
      const hasGlobalAdministratorRole = this.hasGlobalAdministratorRoleSignal();
      const selectedLocation = this.selectedLocationSignal();

      let countries: string[] = [];
      if (hasAdministratorRole) {
        countries = COUNTRIES.filter((r) => r.id === selectedLocation.country)
          .map((r) => r.id)
          .sort((a, b) => (a < b ? -1 : 1));
      }
      if (hasGlobalAdministratorRole) {
        countries = COUNTRIES.map((r) => r.id);
      }
      this.dialog
        .form(ReferenceValueSheetCountryDialogComponent, {
          countries,
          fullscreen: false,
          setPendingChanges: false,
          pushHistoryState: false,
        })
        .pipe(take(1))
        .subscribe((response: { status: DIALOG_RETURN_TYPES; result: { country: string } }) => {
          if (response.status === DIALOG_RETURN_TYPES.confirm) {
            const foundRefValSheet = refValSheets.find((rvs) => refValSheet.id === rvs.id);
            this.store.dispatch(
              refValSheetActions.upsertRefValSheetRequest({
                refValSheet: {
                  ...foundRefValSheet,
                  id: 0,
                  advisorName: user.name,
                  updatedAdvisorName: user.name,
                  created: Utils.toUtcDate(new Date()).toISOString(),
                  updated: Utils.toUtcDate(new Date()).toISOString(),
                  country: response.result.country,
                },
                upsertType: 'copyToOtherCountry',
              }),
            );
          }
        });
    }
  }

  changeCountrySelection(selection: MatSelectChange) {
    this.store.dispatch(
      refValSheetActions.setSelectedCountryRequest({ id: selection.value as string }),
    );
  }
}
